import { ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

export const AssignmentForAssignmentExtensionDialogGql = graphql(`
  fragment AssignmentForAssignmentExtensionDialog on Assignment {
    id
    ends_date
    professional {
      account {
        first_name
      }
    }
    locations {
      name
      id
    }
  }
`);

export const AssignmentForAssignmentDetailsGql = graphql(`
  fragment AssignmentForAssignmentDetails on Assignment {
    id
    status
    can_apply
    pay_per_week_cents: pay_per_week_cents_for_account_id
    one_time_bonus_burden_cents
    vms_partnership
    paused
    missing_condition_ids
    is_w2
    is_travel
    starts_date
    ends_date
    starts_time
    ends_time
    number_of_shifts_per_week
    variable_shift_times
    patient_population
    posting_field_contents
    jobs_detail
    flexible_starts_date
    taxable_amount_cents
    nontaxable_amount_cents
    lodging_per_diem_cents
    mie_per_diem_cents
    taxable_amount_cents
    hours_per_shift
    payout_taxable_hourly_rate_cents
    payout_overtime_multiplier
    payout_on_call_hourly_rate_cents
    lodging_per_diem_cents
    track_unpaid_breaks
    estimated_unpaid_break_time_minutes
    enforce_guaranteed_minimum_hours
    scrubs_provided
    min_years_of_experience
    call_requirements
    contract_total_hours
    guaranteed_hours_string
    facility_number
    cancellation_policy
    needs_item_url
    professional {
      has_item_url
    }
    positions {
      id
      kind
      display_name
      name
    }
    locations {
      id
      name
      timezone_lookup
    }
    clinical_contact {
      first_name
      last_name
    }
    ehr_system {
      id
      label
    }
    specialties {
      id
      label
    }
    surgery_types {
      id
      display_name
    }
    primary_location {
      id
      covid_requirement
      name
      address_first
      address_second
      address_city
      address_zip
      state {
        abbreviation
        covid_requirement
      }
      location_type {
        label
      }
      parking_instructions
      parking_free
      parking_reimbursed
      parking_reimbursement_cents
      parking_validated
      parking_cost_cents
      timezone_lookup
      market_id
      market {
        display_name
      }
      ehr_details
      medication_management_details
      supplies_ppe_details
      scrub_colors
      meal_break_details
      additional_check_in_instructions
    }
    assignment_profile_for_account_id {
      payout_double_overtime_multiplier
      payout_kind
      payout_max_daily_ot_hours
      payout_max_daily_regular_hours
      payout_max_weekly_regular_hours
      payout_overtime_multiplier
    }
  }
`);

const AssignmentForAssignmentDetailsContainerGql = graphql(`
  query GetAssignmentForAssignmentDetails($id: Int!) {
    assignment(id: $id) {
      id
      status
      can_apply
      is_w2
      pay_per_week_cents: pay_per_week_cents_for_account_id
      one_time_bonus_burden_cents
      professional {
        account {
          first_name
          last_name
        }
      }
      is_extension
      ...AssignmentForAssignmentExtensionDialog
      ...AssignmentForAssignmentDetails
    }
  }
`);

const AvailableAssignmentForAssignmentDetailsContainerGql = graphql(`
  query GetAvailableAssignmentForAssignmentDetails($id: Int!) {
    availableAssignment(id: $id) {
      id
      is_extension
      status
      can_apply
      pay_per_week_cents: pay_per_week_cents_for_account_id
      one_time_bonus_burden_cents
      ...AssignmentForAssignmentDetails
    }
  }
`);

type AssignmentProps = ResultOf<typeof AssignmentForAssignmentDetailsContainerGql>;

type AvailableAssignmentProps = ResultOf<
  typeof AvailableAssignmentForAssignmentDetailsContainerGql
>;

export const useAssignmentForAssignmentDetails = (id: number) => {
  const { data, isLoading, refetch } = useGraphQLQuery<AssignmentProps>({
    operationName: 'GetAssignmentForAssignmentDetails',
    queryKey: ['assignment', id],
    query: AssignmentForAssignmentDetailsContainerGql,
    variables: { id },
    enabled: !!id,
  });

  const assignment = data?.assignment;

  return {
    assignment,
    isLoading,
    refetch,
  };
};

export const useAvailableAssignmentForAssignmentDetails = (id: number) => {
  const { data, isLoading, refetch } = useGraphQLQuery<AvailableAssignmentProps>({
    operationName: 'GetAvailableAssignmentForAssignmentDetails',
    queryKey: ['availableAssignment', id],
    query: AvailableAssignmentForAssignmentDetailsContainerGql,
    variables: { id },
    enabled: !!id,
  });

  const availableAssignment = data?.availableAssignment;

  return {
    availableAssignment,
    isLoading,
    refetch,
  };
};
